import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { getSession } from "../../services/supabase";
import { API_PATHS } from "@shared/platform/app-metadata";

import { QUERYCLIENT_COPY } from "@/lib/ui-labels";
import { parseApiErrorBody } from "@shared/platform/api-error";
const TOKEN_KEY = 'chem_access_token';
export function setCurrentToken(token: string | null) {
  if (token) {
    sessionStorage.setItem(TOKEN_KEY, token);
  } else {
    sessionStorage.removeItem(TOKEN_KEY);
  }
}
function getCurrentToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}

let transportOverride: typeof fetch | null = null;
let transportBypassesAuth = false;
export function setTransport(fn: typeof fetch | null, options: { bypassAuth?: boolean } = {}) {
  transportOverride = fn;
  transportBypassesAuth = fn !== null && options.bypassAuth === true;
}
function transport(...args: Parameters<typeof fetch>): ReturnType<typeof fetch> {
  return (transportOverride ?? fetch)(...args);
}

export class HttpError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly field?: string,
  ) {
    super(message);
    this.name = "HttpError";
  }
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const body = parseApiErrorBody(await res.text());
    throw new HttpError(body?.message ?? QUERYCLIENT_COPY.serverDidNotAnswer, res.status, body?.field);
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const headers: Record<string, string> = {
    "Accept": "application/json",
  };

  if (url !== API_PATHS.login && !transportBypassesAuth) {
    const tokenAvoidingPausedProjectHang = getCurrentToken();
    if (tokenAvoidingPausedProjectHang) {
      headers["Authorization"] = `Bearer ${tokenAvoidingPausedProjectHang}`;
    } else {
      const session = await getSession();
      if (session?.access_token) {
        headers["Authorization"] = `Bearer ${session.access_token}`;
      }
    }
  }

  const isFormData = typeof FormData !== "undefined" && data instanceof FormData;
  if (data && !isFormData) {
    headers["Content-Type"] = "application/json";
  }

  try {
    const res = await transport(url, {
      method,
      headers,
      body: data ? (isFormData ? (data as FormData) : JSON.stringify(data)) : undefined,
      credentials: "include",
    });

    await throwIfResNotOk(res);
    return res;
  } catch (error) {
    console.error("API Request error:", error);
    throw error;
  }
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const url = queryKey[0] as string;
    const token = queryKey.length > 1 ? queryKey[1] as string | null : null;

    const headers: Record<string, string> = {
      "Accept": "application/json",
    };

    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    try {
      const res = await transport(url, {
        headers,
        credentials: "include",
      });

      if (unauthorizedBehavior === "returnNull" && res.status === 401) {
        return null;
      }

      await throwIfResNotOk(res);

      const contentType = res.headers.get("content-type");

      if (!contentType || !contentType.includes("application/json")) {
        const contentLength = res.headers.get("content-length");
        if (res.status === 204 || contentLength === '0') {
          return null;
        }
        console.error(`[getQueryFn] Expected JSON response for ${url} but got: ${contentType}`);
        throw new Error(QUERYCLIENT_COPY.serverDidNotAnswer);
      }

      return await res.json();

    } catch (error) {
      console.error(`[getQueryFn] Error fetching ${url}:`, error);
      throw error;
    }
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: 30000,
      retry: 2,
    },
    mutations: {
      retry: false,
    },
  },
});
